import { Category, ReportParameter } from "../../../types/reportTypes";

export const CLIENT_LIST_REPORT_PARAM = "customerId";
export const CLIENT_LIST_PARAM_LABEL = "Client List";

// Single source of truth for "does this report need a client" — driven by the
// report's own declared parameters, NOT by which CategoryCode it happens to sit
// under. If a future report is added under "all-clients" that also carries this
// parameter (miscategorized, or a new per-client report), it still surfaces here
// correctly instead of silently being excluded because of its category label.
export const hasClientListParameter = (
  parameters: ReportParameter[] = [],
): boolean =>
  parameters.some(
    (p) =>
      p.reportParam === CLIENT_LIST_REPORT_PARAM ||
      p.label === CLIENT_LIST_PARAM_LABEL,
  );

// The real catalog nests categories inside categories to an arbitrary depth
// (e.g. "selected" → "asset-reports" → the actual report leaves) — it isn't
// always a flat one-level "category → reports" list. A node is a report leaf
// if it has a ReportCode; otherwise it's a sub-category and must be recursed
// into. Branches are kept only if at least one descendant report qualifies.
export const filterClientContextTree = (nodes: any[] = []): any[] =>
  nodes.reduce((acc: any[], node: any) => {
    if (node?.ReportCode) {
      if (hasClientListParameter(node.parameters)) acc.push(node);
      return acc;
    }

    if (Array.isArray(node?.children) && node.children.length > 0) {
      const filteredChildren = filterClientContextTree(node.children);
      if (filteredChildren.length > 0) {
        acc.push({ ...node, children: filteredChildren });
      }
    }
    return acc;
  }, []);

// `transformReportConfig` (utils/reportTransform.ts) only maps ONE level of
// nesting: for each top-level category it turns direct children into "report"
// objects with `parameters: report.Metadata?.parameters || []`, then passes
// each child's own `children` through completely raw/untransformed. That's
// fine for "custom" (reports sit directly under it), but "selected"'s direct
// children are sub-categories (asset-reports, business-reports, …) — so they
// get `Metadata: undefined` → `parameters: []`, and the REAL leaf reports
// nested inside them never get `Metadata.parameters` copied to `.parameters`
// at all. This recursively normalizes every level instead, so a report has a
// correct `.parameters` regardless of how many sub-category levels it sits
// under — needed both for the customerId filter above and for ReportForm
// later actually rendering the report's real parameters when selected.
export const normalizeNode = (node: any): any => {
  if (node?.ReportCode) {
    return {
      ...node,
      parameters: node.parameters?.length
        ? node.parameters
        : node.Metadata?.parameters || [],
      children: Array.isArray(node.children)
        ? node.children.map(normalizeNode)
        : node.children,
    };
  }
  return {
    ...node,
    children: Array.isArray(node.children)
      ? node.children.map(normalizeNode)
      : node.children,
  };
};

export const normalizeCategories = (categories: any[] = []): Category[] =>
  categories.map((category) => ({
    ...category,
    children: Array.isArray(category.children)
      ? category.children.map(normalizeNode)
      : [],
  }));

// End-to-end helper: normalize the raw /report/category response, then keep
// only the reports that need a client (at any nesting depth), dropping any
// (sub-)category left with zero reports afterwards.
export const buildClientContextCategories = (
  rawCategories: any[] = [],
): Category[] =>
  normalizeCategories(rawCategories)
    .map((category) => ({
      ...category,
      children: filterClientContextTree(category.children),
    }))
    .filter((category) => category.children.length > 0);
